/* PrimeCut: mobile menu drawer, product carousels, campaign countdown. */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE =
    'a[href], button:not([disabled]), summary, input:not([disabled]):not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const SKIP_INERT = ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT'];

  const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

  function initHeader(header) {
    if (header.dataset.pcReady) return;
    header.dataset.pcReady = 'true';

    const section = header.closest('.shopify-section');
    if (section) section.classList.add('pc-sticky-wrap');

    const drawer = document.getElementById(header.dataset.drawerId);
    const toggle = header.querySelector('[data-pc-drawer-toggle]');
    if (!drawer || !toggle) return;

    const panel = drawer.querySelector('.pc-drawer__panel');
    const scroller = drawer.querySelector('.pc-drawer__scroll');
    const label = toggle.querySelector('[data-pc-drawer-label]');
    let inerted = [];
    let hideTimer = null;

    // The drawer opens directly below the sticky header bar, wherever the bar currently is.
    const setTop = () => {
      const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      drawer.style.setProperty('--pc-drawer-top', `${bottom}px`);
    };

    const focusables = () => [toggle, ...panel.querySelectorAll(FOCUSABLE)].filter(isVisible);

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      // Move focus ourselves so it cycles through the toggle and drawer only, never the rest of the header.
      event.preventDefault();
      const index = items.indexOf(document.activeElement);
      let next;
      if (event.shiftKey) next = index <= 0 ? items.length - 1 : index - 1;
      else next = index === -1 || index === items.length - 1 ? 0 : index + 1;
      items[next].focus();
    };

    const onResize = () => {
      if (window.innerWidth >= 990) close(false);
      else setTop();
    };

    function open() {
      window.clearTimeout(hideTimer);
      setTop();
      drawer.hidden = false;
      if (scroller) scroller.scrollTop = 0;
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      if (label) label.textContent = label.dataset.closeLabel;
      document.documentElement.classList.add('pc-scroll-lock');
      inerted = [...document.body.children].filter(
        (el) => el !== section && !el.contains(header) && !SKIP_INERT.includes(el.tagName) && !el.inert
      );
      inerted.forEach((el) => {
        el.inert = true;
      });
      document.addEventListener('keydown', onKeydown);
      window.addEventListener('resize', onResize);
      panel.focus({ preventScroll: true });
    }

    function close(returnFocus = true) {
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      if (label) label.textContent = label.dataset.openLabel;
      document.documentElement.classList.remove('pc-scroll-lock');
      inerted.forEach((el) => {
        el.inert = false;
      });
      inerted = [];
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('resize', onResize);
      hideTimer = window.setTimeout(() => {
        drawer.hidden = true;
      }, reduceMotion.matches ? 0 : 320);
      if (returnFocus) toggle.focus();
    }

    toggle.addEventListener('click', () => (drawer.classList.contains('is-open') ? close() : open()));
    drawer.querySelectorAll('[data-pc-drawer-close]').forEach((el) => el.addEventListener('click', () => close()));
    drawer.querySelectorAll('a[href]').forEach((link) => link.addEventListener('click', () => close(false)));
  }

  function initCarousel(carousel) {
    if (carousel.dataset.pcReady) return;
    const track = carousel.querySelector('[data-pc-track]');
    if (!track) return;
    carousel.dataset.pcReady = 'true';
    const prev = carousel.querySelector('[data-pc-prev]');
    const next = carousel.querySelector('[data-pc-next]');

    const stepSize = () => {
      const item = track.children[0];
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      carousel.classList.toggle('is-static', max <= 0);
    };

    const go = (direction) =>
      track.scrollBy({ left: direction * stepSize(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });

    if (prev) prev.addEventListener('click', () => go(-1));
    if (next) next.addEventListener('click', () => go(1));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function initCountdown(el) {
    if (el.dataset.pcReady) return;
    el.dataset.pcReady = 'true';
    const end = Number(el.dataset.end) * 1000;
    const output = el.querySelector('[data-pc-countdown-value]');
    if (!end || !output) {
      el.remove();
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const left = end - Date.now();
      if (left <= 0) {
        el.remove();
        return false;
      }
      const hours = Math.floor(left / 3600000);
      const minutes = Math.floor((left % 3600000) / 60000);
      const seconds = Math.floor((left % 60000) / 1000);
      output.textContent = `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
      return true;
    };
    if (!tick()) return;
    el.hidden = false;
    const timer = window.setInterval(() => {
      if (!tick()) window.clearInterval(timer);
    }, 1000);
  }

  function init(root) {
    root.querySelectorAll('[data-pc-header]').forEach(initHeader);
    root.querySelectorAll('[data-pc-carousel]').forEach(initCarousel);
    root.querySelectorAll('[data-pc-countdown]').forEach(initCountdown);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
